/**
 * BrandContext — fetches per-deployment branding from /api/config once at
 * boot and exposes it to every component via useBrand().
 *
 * Defaults preserve the current AI Tutor — Health Analytics deployment so
 * the UI renders something sensible even before /api/config returns.
 *
 * To re-skin the app for a new institution, edit only backend/.env — no
 * frontend rebuild required.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_BRAND = {
  platform_name: 'CourseForge',
  product_name: 'AI Tutor',
  institution_name: 'Institute of Analytics',
  institution_short_name: 'Institute of Analytics',
  course_title: 'AI Tutor',
  course_subtitle: 'Health Analytics - Supervised Algorithms and Unsupervised Algorithms',
  course_description:
    'Module 1 to Module 5 is Supervised Algorithms; Module 6 to Module 10 is Unsupervised Algorithms',
  ai_tutor_persona_name: 'AITutor',
  parent_org_name: 'Institute of Analytics',
  parent_org_url: 'https://instituteofanalytics.com',
  primary_color: '#1A2B4C',
  secondary_color: '#E0B73D',
  logo_url: '/branding/ai-tutor-logo.png',
  favicon_url: '/favicon.ico?v=2',
  scope_label_topic: 'Topic',
  scope_label_course: 'Whole Course',
  scope_label_training: 'Claude Training',
  html_title: 'AI Tutor - Health Analytics - Supervised Algorithms',
  html_description: 'AI Tutor - Health Analytics - Supervised Algorithms',
};

const BrandContext = createContext(DEFAULT_BRAND);

export const BrandProvider = ({ children }) => {
  const [brand, setBrand] = useState(DEFAULT_BRAND);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/config`);
        if (cancelled) return;
        const merged = { ...DEFAULT_BRAND, ...res.data };
        setBrand(merged);
        // Apply HTML <title> + meta description live.
        if (merged.html_title) document.title = merged.html_title;
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc && merged.html_description) {
          metaDesc.setAttribute('content', merged.html_description);
        }
        // Apply CSS variables so existing classes that reference
        // --unthsc-green / --unthsc-blue continue to work but become
        // per-deployment configurable.
        const root = document.documentElement;
        if (merged.primary_color) {
          root.style.setProperty('--unthsc-green', merged.primary_color);
          root.style.setProperty('--brand-primary', merged.primary_color);
        }
        if (merged.secondary_color) {
          root.style.setProperty('--unthsc-blue', merged.secondary_color);
          root.style.setProperty('--brand-secondary', merged.secondary_color);
        }
      } catch (err) {
        // Silent: defaults already applied.
         
        console.warn('[brand] /api/config fetch failed, using defaults', err?.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>
  );
};

export const useBrand = () => useContext(BrandContext);
